import { EmptyState, LocaleProvider, ToastProvider } from '@nolens/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { ApiError, setRequestLocale } from './lib/api';
import { browserLocale, useAppLocale } from './lib/locale';
import { isConfigured } from './lib/config';
import { SessionProvider } from './lib/session';
import { router } from './router';
import { pathLocale } from './i18n/landing';
import { installErrorReporting } from './lib/errors';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Não repetir erros de permissão/inexistência; só falhas transitórias.
      retry: (count, err) =>
        !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

/** Língua da interface (conta ou browser) para toda a app e para os pedidos à API. */
function AppLocale({ children }: { children: ReactNode }) {
  // Nas páginas públicas com endereço próprio de cada língua, manda o endereço
  const path = useSyncExternalStore(
    (cb) => router.subscribe(cb),
    () => router.state.location.pathname,
  );
  const account = useAppLocale();
  const locale = pathLocale(path) ?? account;
  setRequestLocale(locale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return <LocaleProvider locale={locale}>{children}</LocaleProvider>;
}

installErrorReporting();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {isConfigured ? (
      <SessionProvider>
        <QueryClientProvider client={queryClient}>
          <AppLocale>
            <ToastProvider>
              <RouterProvider router={router} />
            </ToastProvider>
          </AppLocale>
        </QueryClientProvider>
      </SessionProvider>
    ) : (
      // Só para quem instala a app (falta configuração), não para quem a usa.
      <EmptyState
        // i18n-ignore
        title={browserLocale() === 'en' ? 'Missing configuration' : 'Configuração em falta'}
        // i18n-ignore
        description="VITE_SUPABASE_URL, VITE_SUPABASE_PUBLISHABLE_KEY, VITE_API_URL."
      />
    )}
  </StrictMode>,
);
